import React, { useState, useCallback } from "react";
import { Button } from "reactstrap";

import ModalOcorrencia from "../ModalOcorrencia";
import MapaRotaEstimada from "../MapaRotaEstimada";
import { formatCep } from "../../../../../utils/formatDoc";

const getDisplayValue = (value) => value || "-";

const getTransportadoraName = (item) =>
  item.transportadora ||
  item.nome_transportadora ||
  item.transportadora_nome ||
  item.nomeTransportadora ||
  item.transportadoraFantasia ||
  item.fantasia_transportadora ||
  null;

const normalizeAddressPart = (value) => {
  if (!value) return null;
  const normalized = String(value).trim();
  if (!normalized || ["NA", "N/A", "NULL", "-"].includes(normalized.toUpperCase())) {
    return null;
  }
  return normalized;
};

const getDeliveryAddress = (item) => {
  if (item.endereco_entrega) return item.endereco_entrega;

  const parts = [
    item.logradouro_entrega,
    item.numero_entrega,
    item.complemento_entrega,
    item.bairro_entrega,
  ].map(normalizeAddressPart).filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : null;
};

const hasDate = (value) => Boolean(value && value !== "-");

// PO1-2818: o status NÃO é mais derivado aqui a partir de datas cruas.
// A regra canônica dos 7 estados vive na API (PO1-2820, `deliveryStatusSql.js`
// no backend_hub) e é a mesma que o dashboard e o Autopilot consomem. Aqui só
// traduzimos `status`/`statusLabel` em apresentação: tom visual, título e uma
// frase de contexto. Mudança de regra é no backend, não neste arquivo.
//
// PO1-2374 (H1-B): `etiqueta_emitida` e `despachado` são os estados pré-entrega
// do módulo de despacho. Por padrão a API pública os converte em `pendente`;
// só com o parâmetro do embarcador ligado eles chegam como estão, e aí têm de
// ser tratados aqui (senão caíam no "Não encontramos informações").
export const TONE_POR_STATUS = {
  entregue: "success",
  devolvida: "danger",
  atrasada: "danger",
  em_transito: "info",
  pendente: "neutral",
  sem_previsao: "neutral",
  retirada: "success",
  entrega_propria: "info",
  etiqueta_emitida: "neutral",
  despachado: "info",
  nao_encontrada: "neutral",
};

export const TITULO_POR_STATUS = {
  entregue: "Entrega realizada",
  devolvida: "Entrega devolvida",
  atrasada: "Entrega em atraso",
  em_transito: "Sua entrega está a caminho",
  pendente: "Pedido em preparação",
  sem_previsao: "Entrega registrada",
  retirada: "Retirada pelo cliente",
  entrega_propria: "Entrega pela própria loja",
  etiqueta_emitida: "Pedido em preparação",
  despachado: "Seu pedido está saindo",
  nao_encontrada: "Entrega não encontrada",
};

// Progresso da barra por status. O cálculo antigo era por presença de data e
// nunca passava de 66,68%, então nem uma entrega concluída chegava ao fim da
// barra.
export const PROGRESSO_POR_STATUS = {
  pendente: 0,
  sem_previsao: 0,
  nao_encontrada: 0,
  etiqueta_emitida: 0,
  despachado: 25,
  em_transito: 50,
  atrasada: 50,
  entregue: 100,
  devolvida: 100,
  retirada: 100,
  entrega_propria: 50,
};

export const descricaoDoStatus = (item, status) => {
  switch (status) {
    case "entregue":
      if (item.atrasoNaEntrega && hasDate(item.dt_previsao_entrega)) {
        return `A entrega foi concluída em ${item.dt_entrega}, após a previsão de ${item.dt_previsao_entrega}.`;
      }
      return hasDate(item.dt_entrega)
        ? `A entrega foi concluída em ${item.dt_entrega}.`
        : "A entrega foi concluída.";
    case "devolvida":
      return "A mercadoria foi devolvida ao remetente. Fale com a loja para saber os próximos passos.";
    case "atrasada":
      return `A previsão era ${item.dt_previsao_entrega}. Consulte o histórico para acompanhar as atualizações.`;
    case "em_transito":
      return `Previsão de entrega em ${item.dt_previsao_entrega}.`;
    case "sem_previsao":
      return "A entrega foi registrada e ainda não tem previsão confirmada pela transportadora.";
    case "pendente":
      return "A entrega foi registrada e aguarda novas atualizações.";
    // PO1-3401: sem transportadora contratada.
    case "retirada":
      return "A mercadoria foi retirada pelo próprio destinatário.";
    case "entrega_propria":
      return "A entrega é feita pela própria loja. Fale com ela para combinar o recebimento.";
    // PO1-2374: pré-entrega (módulo de despacho).
    case "etiqueta_emitida":
      return "Seu pedido já está com etiqueta e logo será coletado pela transportadora.";
    case "despachado":
      return "Seu pedido foi entregue à transportadora e aguarda a primeira atualização.";
    default:
      return "Não encontramos informações de entrega para este documento.";
  }
};

export const getStatusApresentacao = (item) => {
  const status = item.status || "nao_encontrada";
  const entregueForaDoPrazo = status === "entregue" && Boolean(item.atrasoNaEntrega);

  // Entregue fora do prazo tem tom próprio — é a única variação que não sai
  // direto do status.
  const tone = entregueForaDoPrazo
    ? "warning"
    : TONE_POR_STATUS[status] || "neutral";

  // ...e tem rótulo próprio. O selo é o que o cliente final lê de relance, e a
  // cor sozinha não comunica nada para quem não conhece a convenção (nem para
  // quem tem daltonismo). Deixar só "Entregue" num pedido que atrasou era a
  // única parte da página onde a cor dizia algo que a palavra não dizia.
  //
  // `statusLabel` continua vindo da API com os 7 estados canônicos: este caso
  // NÃO vira um oitavo estado na regra compartilhada, é composição de tela a
  // partir de `atrasoNaEntrega`, que a API já devolve. "Fora do prazo" é o
  // vocabulário do próprio Flow (relatório EntregueForaPrazo).
  const label = entregueForaDoPrazo
    ? "Entregue fora do prazo"
    : item.statusLabel || TITULO_POR_STATUS[status] || "Em andamento";

  return {
    tone,
    label,
    title: entregueForaDoPrazo
      ? "Entrega realizada fora do prazo"
      : TITULO_POR_STATUS[status] || "Acompanhe sua entrega",
    description: descricaoDoStatus(item, status),
    progress: PROGRESSO_POR_STATUS[status] || 0,
  };
};

// PO1-3401: retirada fecha a jornada (não houve transporte); entrega própria
// está em andamento com a própria loja. PO1-2374: despachado já saiu para a
// transportadora (em transporte); etiqueta emitida ainda está na loja.
const FINALIZADO = ["entregue", "devolvida", "retirada"];
const EM_TRANSPORTE = ["em_transito", "atrasada", "entrega_propria", "despachado"];

export const buildSteps = (item, status) => [
  {
    label: "Pedido emitido",
    date: item.dt_emissao,
    state: hasDate(item.dt_emissao) ? "done" : "pending",
  },
  {
    label: "Em transporte",
    date: item.dt_previsao_entrega,
    state: FINALIZADO.includes(status) ? "done" : EM_TRANSPORTE.includes(status) ? "current" : "pending",
  },
  {
    label: status === "devolvida" ? "Devolução" : status === "retirada" ? "Retirada" : "Entrega",
    date: item.dt_entrega,
    state: FINALIZADO.includes(status) ? "done" : "pending",
  },
];

const SummaryItem = ({ label, value }) => (
  <div className="public-tracking-summary-item">
    <span>{label}</span>
    <strong>{getDisplayValue(value)}</strong>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="public-tracking-detail-item">
    <span>{label}</span>
    <strong>{getDisplayValue(value)}</strong>
  </div>
);

// `chaveNf` desce da página porque o histórico público passou a ser consultado
// pela CHAVE, não pelo id sequencial da nota (H2 PO1-2920). O id continua sendo
// o que o cliente vê ("Rastreio Everlog #"), só não é mais credencial de busca.
const CardResult = ({ data, chaveNf }) => {
  const [modal, setModal] = useState(false);
  const [notaSelecionada, setNotaSelecionada] = useState(null);

  const openOccurrences = useCallback((nota) => {
    setNotaSelecionada(nota);
    setModal(true);
  }, []);

  const closeOccurrences = useCallback(() => {
    setModal(false);
  }, []);

  return (
    <section className="public-tracking-results">
      <ModalOcorrencia
        modal={modal}
        toggle={closeOccurrences}
        notaFiscal={notaSelecionada}
      />

      {data.map((item, index) => {
        const status = item.status || "nao_encontrada";
        const apresentacao = getStatusApresentacao(item);
        const steps = buildSteps(item, status);
        const transportadora = getTransportadoraName(item);
        const enderecoEntrega = getDeliveryAddress(item);
        const ultimaOcorrencia = item.ultimaOcorrencia;

        return (
          <article
            key={item.id || index}
            className={`public-tracking-card public-tracking-card--${apresentacao.tone}`}
          >
            <div className="public-tracking-card__header">
              <div>
                <span className="public-tracking-card__tracking">
                  Rastreio Everlog #{getDisplayValue(item.id)}
                </span>
                <h2>{apresentacao.title}</h2>
                <p>{apresentacao.description}</p>
              </div>
              <span
                className={`public-tracking-status public-tracking-status--${apresentacao.tone}`}
              >
                {apresentacao.label}
              </span>
            </div>

            <div className="public-tracking-summary">
              <SummaryItem label="Pedido" value={item.numero_pedido} />
              <SummaryItem label="Nota fiscal" value={item.numero} />
              <SummaryItem label="Previsão" value={item.dt_previsao_entrega} />
              <SummaryItem label="Entrega" value={item.dt_entrega} />
            </div>

            <div
              className={`public-tracking-timeline public-tracking-timeline--${apresentacao.tone}`}
              style={{ "--public-tracking-progress": `${apresentacao.progress}%` }}
            >
              <div className="public-tracking-timeline__line" />
              <div className="public-tracking-timeline__progress" />
              {steps.map((step, stepIndex) => (
                <div
                  key={step.label}
                  className={`public-tracking-timeline__step is-${step.state}`}
                >
                  <span>{stepIndex + 1}</span>
                  <strong>{step.label}</strong>
                  <small>{getDisplayValue(step.date)}</small>
                </div>
              ))}
            </div>

            {ultimaOcorrencia ? (
              <div className="public-tracking-last-update">
                <div className="public-tracking-last-update__marker">
                  <i className="tim-icons icon-delivery-fast" aria-hidden="true" />
                </div>
                <div>
                  <span>Última atualização</span>
                  <strong>{ultimaOcorrencia.nome}</strong>
                  <small>{getDisplayValue(item.dt_ultima_ocorrencia)}</small>
                </div>
              </div>
            ) : null}

            <MapaRotaEstimada
              origem={{
                ibge: item.ibge_origem,
                cidade: item.cidade_origem,
                uf: item.uf_origem,
              }}
              destino={{
                ibge: item.ibge_entrega,
                cidade: item.cidade_entrega,
                uf: item.uf_entrega,
              }}
            />

            <div className="public-tracking-details">
              <DetailItem label="Remetente" value={item.remetente} />
              {transportadora ? (
                <DetailItem label="Transportadora" value={transportadora} />
              ) : null}
              <DetailItem label="Destinatário" value={item.destinatario} />
              {enderecoEntrega ? (
                <DetailItem label="Endereço de entrega" value={enderecoEntrega} />
              ) : null}
              {/* CEP em campo próprio, não embutido no endereço: e-commerce
                  confere e copia o CEP o tempo todo, e dentro da linha de
                  endereço ele fica difícil de achar e de selecionar. */}
              {item.cep_entrega ? (
                <DetailItem label="CEP" value={formatCep(item.cep_entrega)} />
              ) : null}

              <div className="public-tracking-fiscal-details">
                <div className="public-tracking-fiscal-details__content">
                  <span>Chave de acesso NFe</span>
                  <strong>{getDisplayValue(item.chave_acesso)}</strong>
                </div>
              </div>
            </div>

            <div className="public-tracking-card__footer">
              <Button
                className="public-tracking-history-button"
                type="button"
                onClick={() => openOccurrences({ id: item.id, chaveNf })}
              >
                <i className="tim-icons icon-delivery-fast" />
                Ver histórico da entrega
              </Button>
            </div>
          </article>
        );
      })}
    </section>
  );
};

export default CardResult;
